import React, { JSX } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  Divider,
  Grid,
  LinearProgress,
  Typography,
} from '@mui/material';
import { Psychology } from '@mui/icons-material';
import * as Types from 'types/types';
import { StatusBox, StatusIcon } from 'components/ui/StatusIcon';
import { Scrollable } from './Scrollable';
import { StyledMarkdown } from './StyledMarkdown';
import { JobProcessingPhase, PHASE_LABEL } from './jobCreatorUtils';

export function JobCreatorProcessing(props: {
  isProcessing: boolean;
  jobStatus: string;
  jobStatusType: Types.ApiActivityType | null;
  thinkingMarkdown: string;
  generatedMarkdown: string;
  processingPhase: JobProcessingPhase;
  jobTitle: string;
  company: string;
  summary: string;
}): JSX.Element {
  const hasProgress =
    props.isProcessing || props.jobStatus || props.thinkingMarkdown || props.generatedMarkdown;
  if (!hasProgress) return <></>;

  const thinkingContent = props.thinkingMarkdown || props.jobStatus || 'Starting job analysis...';

  return (
    <Card elevation={0} sx={{ mt: 3, border: '1px solid', borderColor: 'divider' }}>
      <CardHeader
        title="Job Analysis"
        subheader={PHASE_LABEL[props.processingPhase]}
        avatar={
          props.jobStatusType ? (
            <StatusIcon type={props.jobStatusType} />
          ) : (
            <Psychology color="primary" />
          )
        }
        sx={{ pb: 1 }}
      />
      <CardContent sx={{ pt: 0 }}>
        <StatusBox>
          {props.jobStatusType && <StatusIcon type={props.jobStatusType} />}
          <Typography variant="body2" sx={{ ml: 1 }}>
            {props.jobStatus || 'Processing...'}
          </Typography>
        </StatusBox>
        {props.isProcessing && <LinearProgress sx={{ mt: 1.5 }} />}
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid size={{ xs: 12, md: props.generatedMarkdown ? 6 : 12 }}>
            <MarkdownPane
              title="Thinking"
              content={thinkingContent}
              backgroundColor="background.default"
              streaming={props.isProcessing}
            />
          </Grid>
          {props.generatedMarkdown && (
            <Grid size={{ xs: 12, md: 6 }}>
              <MarkdownPane
                title="Markdown"
                content={props.generatedMarkdown}
                backgroundColor="background.paper"
                streaming={props.isProcessing && props.processingPhase === 'markdown'}
              />
            </Grid>
          )}
        </Grid>
        <DetailPreview jobTitle={props.jobTitle} company={props.company} summary={props.summary} />
      </CardContent>
    </Card>
  );
}

function MarkdownPane(props: {
  title: string;
  content: string;
  backgroundColor: string;
  streaming: boolean;
}): JSX.Element {
  return (
    <>
      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
        {props.title}
      </Typography>
      <Scrollable
        sx={{
          maxHeight: 260,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
          p: 1.5,
          bgcolor: props.backgroundColor,
        }}
      >
        <StyledMarkdown content={props.content} streaming={props.streaming} />
      </Scrollable>
    </>
  );
}

function DetailPreview(props: { jobTitle: string; company: string; summary: string }): JSX.Element {
  if (!props.jobTitle && !props.company && !props.summary) return <></>;

  return (
    <>
      <Divider sx={{ my: 2 }} />
      <Grid container spacing={2}>
        {props.jobTitle && <PreviewItem label="Title" value={props.jobTitle} />}
        {props.company && <PreviewItem label="Company" value={props.company} />}
        {props.summary && <PreviewItem label="Summary" value={props.summary} />}
      </Grid>
    </>
  );
}

function PreviewItem(props: { label: string; value: string }): JSX.Element {
  return (
    <Grid size={{ xs: 12, md: 4 }}>
      <Typography variant="caption" color="text.secondary">
        {props.label}
      </Typography>
      <Typography variant="body2">{props.value}</Typography>
    </Grid>
  );
}
